import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import {
	DataSecurityTct,
	TransactionType,
	TransactionWithSecurityTct,
} from '@dealstack/shared/lib/generated';
import { constructTransactionSentence } from '@dealstack/frontend/shared/utils/constructTransactionSentence.ts';
import type { UploadEntityDetails } from '@dealstack/frontend/shared/components/Documents/types';

export interface SelectOption {
	label: string;
	value: string;
}

export const getEntityNameById = (
	entityId: string | undefined,
	options?: SelectOption[]
): string => {
	if (!entityId) {
		return LONG_DASH;
	}
	const entity = options?.find((option) => option.value === entityId);
	return entity ? entity.label : LONG_DASH;
};

const getPersonName = (personId: string | undefined, personOptions?: SelectOption[]): string =>
	getEntityNameById(personId, personOptions);

export const getMipName = (
	mipId: string | undefined,
	mipOptions: SelectOption[],
	fallback = 'No MIP'
): string => {
	if (!mipId) {
		return fallback;
	}
	const entity = mipOptions.find((option) => option.value === mipId);
	return entity ? entity.label : fallback;
};

const getSecurityById = (
	securityId: string,
	securities?: DataSecurityTct[]
): DataSecurityTct | undefined => {
	return securities?.find((sec) => sec.id === securityId);
};

export const getSecurityNameById = (securityId: string, securities?: DataSecurityTct[]): string => {
	const security = getSecurityById(securityId, securities);
	return security ? security.name : LONG_DASH;
};

export const getSecurityCurrencyById = (
	securityId: string,
	securities?: DataSecurityTct[]
): string | undefined => {
	const security = getSecurityById(securityId, securities);
	return security?.currency;
};

export const buildTransactionSentence = (
	transaction: TransactionWithSecurityTct,
	securities?: DataSecurityTct[],
	personOptions?: SelectOption[]
) => {
	// The entity-scoped list misses the transaction's own security on a holder's page.
	const security = getSecurityById(transaction.securityId, securities) ?? transaction.security;

	let preEventSecurityName: string | undefined = undefined;
	let securityName = security?.name || '';

	if (transaction.type === TransactionType.CONVERSION_SHARE_CAPITAL) {
		const targetId = transaction.conversionTargetSecurityId;
		if (!targetId) {
			securityName = LONG_DASH;
		} else {
			const targetName = getSecurityNameById(targetId, securities);
			preEventSecurityName = security?.name;
			securityName = targetName;
		}
	}

	return constructTransactionSentence({
		securityName,
		transactionDate: transaction.date,
		transactionType: transaction.type,
		securityIssuedIn: security?.issuedIn,
		securityCurrency: security?.currency,
		buyerName: getPersonName(transaction.destinationId, personOptions),
		sellerName: getPersonName(transaction.sourceId, personOptions),
		units: transaction.units ?? 0,
		cost: transaction.cost ?? 0,
		securityUnitDenominator: security?.unitDenominator || 1,
		preEventHoldingUnits: undefined,
		postEventHoldingUnits: undefined,
		preEventSecurityName,
	});
};

/**
 * Describes the transaction a document upload starts from. Reads its own security, not the
 * entity-scoped list, which misses it on a holder's page.
 */
export const buildUploadEntityDetails = (
	transaction: TransactionWithSecurityTct,
	personOptions?: SelectOption[]
): UploadEntityDetails | undefined => {
	const { security } = transaction;
	if (!security) {
		return undefined;
	}

	return {
		transactionId: transaction.id,
		buyer: getPersonName(transaction.destinationId, personOptions),
		seller: getPersonName(transaction.sourceId, personOptions),
		securityName: security.name,
		transactionType: transaction.type,
		transactionDate: transaction.date,
		units: transaction.units,
		price: transaction.cost,
		issuerName: security.issuer.displayName,
		securityIssuedIn: security.issuedIn,
		securityCurrency: security.currency,
		securityUnitDenominator: security.unitDenominator,
	};
};
